// Peças compartilhadas das telas de suporte: formatação, listas, painel do card,
// janelas de "Corrigir dados" e "Finalizar".
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem,
  TextField, Typography, makeStyles,
} from "@material-ui/core";
import { Send, FileCopyOutlined, EditOutlined, CheckCircleOutline, Replay, Add, CallSplit } from "@material-ui/icons";
import { usePermissao, usePermissoes, tem } from "./permissoes";

import suporteApi, { agoraServidor, mensagemErro, urlQr } from "./api";
import { FormulariosDoCard } from "./Formularios";
import CampoCard from "./CampoCard";
import Galeria from "./Galeria";
import { FormCadastro } from "./CadastroCliente";

// conta do card que não está na Base clientes: cadastra na hora e liga o cliente ao card
const CadastrarNaBase = ({ card, onPreencher }) => {
  const [registro, setRegistro] = useState(null);
  if (!card.conta || card.cliente_id || !onPreencher) return null;
  const abrir = async () => {
    // "B621 DP L2621 AGUAS CLARAS" -> conta B621, nome DP L2621 AGUAS CLARAS
    const m = String(card.conta).trim().match(/^([A-Z]{0,3}\d{2,6}[A-Z]?)\s*[-–]?\s+(.+)$/i);
    const conta = m ? m[1].toUpperCase() : String(card.conta).trim();
    try {  // conta já cadastrada: só liga ao card (não duplica)
      const { data } = await suporteApi.get(`/api/clientes/conta/${encodeURIComponent(conta)}`);
      if (data.length) {
        await onPreencher({ conta: data[0].conta, cliente_id: data[0].id });
        toast.info(`Conta ${data[0].conta} já está na base (${data[0].nome}): ligada ao card`);
        return;
      }
    } catch {
      // não encontrada: segue para o cadastro
    }
    setRegistro({ status: "ATIVO", conta, nome: m ? m[2] : "" });
  };
  return (
    <>
      <Button size="small" color="primary" variant="outlined" startIcon={<Add />} onClick={abrir}
        style={{ marginLeft: 8, padding: "0 8px", fontSize: 11.5 }}>
        Cadastrar na base
      </Button>
      <FormCadastro registro={registro} onClose={() => setRegistro(null)}
        onSalvo={(cli) => onPreencher({ conta: cli.conta, cliente_id: cli.id })} />
    </>
  );
};
import { getBackendUrl } from "../config";

// mídia de uma mensagem do WhatsApp no andamento (arquivo servido pelo Whaticket em /public)
const MidiaAndamento = ({ midia }) => {
  const url = `${String(getBackendUrl() || "").replace(/\/$/, "")}/public/${encodeURIComponent(midia.arquivo)}`;
  const legenda = midia.legenda && <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{midia.legenda}</div>;
  if (midia.tipo === "audio" || /\.(ogg|opus|mp3|m4a|aac|wav)$/i.test(midia.arquivo)) {
    return <><audio controls preload="none" src={url} style={{ width: "100%", maxWidth: 320, marginTop: 4 }} />{legenda}</>;
  }
  return <><div style={{ marginTop: 4 }}><Galeria itens={[{ url, titulo: midia.legenda || midia.arquivo }]} /></div>{legenda}</>;
};

// ------------------------------------------------------------------ formatação
export const dataBR = (iso, soData = false) => {
  if (!iso) return "-";
  const [d, h = ""] = String(iso).split("T");
  const [a, m, dia] = d.split("-");
  return soData ? `${dia}/${m}/${a}` : `${dia}/${m}/${a} ${h.slice(0, 5)}`.trim();
};

export const duracao = (min) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h${String(min % 60).padStart(2, "0")}`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

export const haQuanto = (iso) => {
  if (!iso) return "";
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  if (dias < 60) return `há ${dias} dias`;
  if (dias < 730) return `há ${Math.round(dias / 30)} meses`;
  return `há ${Math.round(dias / 365)} anos`;
};

export const minutosDesde = (iso) =>
  Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

export const agoraLocal = () => {
  const d = agoraServidor();  // hora do servidor (o relógio do PC pode estar errado)
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export const interno = (a) => a.categoria === "interno";

export const semCliente = (a) => !(a.campos || {}).cliente;
export const faltandoNoCard = (a) => (interno(a) ? [] : [
  [(!a.qra && a.qra !== 0), "QRA"], [!a.os, "O.S"], [!a.tipo, "tipo"], [!a.sistema, "sistema"],
  [!a.cliente_id, "conta (da Base clientes)"]].filter(([f]) => f).map(([, n]) => n))
  .concat(semCliente(a) ? ["Cliente (campo Cliente no card)"] : []);
export const incompleto = (a) =>
  // conta: tem de estar localizada na Base clientes (preenchida pelo suporte); Cliente obrigatório para fechar
  (interno(a) ? semCliente(a)
    : (!a.qra && a.qra !== 0) || !a.os || !a.cliente_id || !a.tipo || !a.sistema || semCliente(a));

export const SITUACAO = { ABERTO: "Em andamento", PENDENTE: "Pendente", FECHADO: "Concluído" };

export const copiar = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    const t = document.createElement("textarea");
    t.value = texto;
    document.body.appendChild(t);
    t.select();
    document.execCommand("copy");
    t.remove();
  }
  toast.success("Copiado");
};

// ------------------------------------------------------------------ listas (tipo, sistema, status)
let listasCache = null;
export const carregarListas = (forcar = false) => {
  if (!listasCache || forcar) listasCache = suporteApi.get("/api/listas").then((r) => r.data);
  return listasCache;
};

export const useListas = () => {
  const [listas, setListas] = useState({ analista: [], tipo: [], sistema: [], status: [], demanda: [], causa: [] });
  useEffect(() => {
    carregarListas().then(setListas).catch(() => {});
  }, []);
  return listas;
};

// ------------------------------------------------------------------ estilos
const useStyles = makeStyles((theme) => ({
  linha: { display: "flex", gap: 8, padding: "3px 0", fontSize: 13 },
  rotulo: { color: theme.palette.text.secondary, minWidth: 96, flex: "none" },
  valor: { wordBreak: "break-word" },
  chips: { display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" },
  wa: { background: "#1f9d55", color: "#fff" },
  falta: { borderStyle: "dashed", color: theme.palette.warning.main, borderColor: theme.palette.warning.main },
  andamento: {
    borderLeft: `3px solid ${theme.palette.primary.main}`, padding: "2px 0 2px 10px", margin: "8px 0", fontSize: 13,
  },
  andamentoWa: { borderLeftColor: "#1f9d55" },
  meta: { color: theme.palette.text.secondary, fontSize: 11.5 },
  protocolo: {
    display: "flex", gap: 14, alignItems: "center", background: theme.palette.action.hover,
    borderRadius: 8, padding: 12, margin: "10px 0", flexWrap: "wrap",
  },
  codigo: { fontFamily: "monospace", fontSize: 17, fontWeight: 700, wordBreak: "break-all" },
  acoes: { display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0" },
}));

const Linha = ({ rotulo, children }) => {
  const c = useStyles();
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className={c.linha}>
      <span className={c.rotulo}>{rotulo}</span>
      <span className={c.valor}>{children}</span>
    </div>
  );
};

export const Etiquetas = ({ card }) => {
  const c = useStyles();
  return (
    <div className={c.chips}>
      <Chip size="small" label={SITUACAO[card.status] || card.status}
        color={card.status === "ABERTO" ? "primary" : "default"} />
      {interno(card) && <Chip size="small" label="Interno" color="secondary" />}
      {(card.origem === "whatsapp" || card.whatsapp_ticket_id) && <Chip size="small" label="WhatsApp" className={c.wa} />}
      {card.tipo && <Chip size="small" variant="outlined" label={card.tipo} />}
      {card.sistema && <Chip size="small" variant="outlined" label={card.sistema} />}
      {card.status !== "FECHADO" && incompleto(card) && (
        <Chip size="small" variant="outlined" label="completar dados" className={c.falta} />
      )}
    </div>
  );
};

// Valor que vira campo ao clicar (Enter salva, Esc cancela). Só com o card aberto.
const CampoEditavel = ({ texto, valorInicial, opcoes, tipo = "text", placeholder, onSalvar }) => {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState("");
  const [salvando, setSalvando] = useState(false);
  if (!onSalvar) return <>{texto || "a informar"}</>;
  if (!editando)
    return (
      <span
        role="button" tabIndex={0} title="Clique para preencher"
        style={{ cursor: "pointer", borderBottom: "1px dashed currentColor", color: texto ? undefined : "#e65100" }}
        onClick={() => { setValor(valorInicial ?? ""); setEditando(true); }}
        onKeyDown={(e) => e.key === "Enter" && (setValor(valorInicial ?? ""), setEditando(true))}
      >
        {texto || "a informar — clique para preencher"}
      </span>
    );
  const salvar = async (v = valor) => {
    if (String(v).trim() === "" || String(v) === String(valorInicial ?? "")) return setEditando(false);
    setSalvando(true);
    const ok = await onSalvar(v);
    setSalvando(false);
    if (ok) setEditando(false);
  };
  return (
    <TextField
      autoFocus size="small" fullWidth value={valor} placeholder={placeholder} disabled={salvando}
      type={opcoes ? undefined : tipo} select={!!opcoes}
      SelectProps={opcoes ? { open: true, onClose: () => setEditando(false) } : undefined}
      onChange={(e) => { setValor(e.target.value); if (opcoes) salvar(e.target.value); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); salvar(); }
        if (e.key === "Escape") setEditando(false);
      }}
      onBlur={() => !opcoes && salvar()}
      InputProps={opcoes ? undefined : {
        endAdornment: salvando ? <CircularProgress size={16} /> : (
          <InputAdornment position="end">
            <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => salvar()}>
              <CheckCircleOutline fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    >
      {opcoes && opcoes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
  );
};

export const DadosCard = ({ card, onPreencher }) => {
  const listas = useListas();
  const gestao = usePermissao("cards.corrigir_causa_finalizado");
  const cl = card.cliente || {};
  const end = [cl.endereco, cl.bairro, cl.cidade, cl.uf].filter(Boolean).join(", ");
  const equip = [cl.modelo, cl.marca, cl.mac && `MAC ${cl.mac}`].filter(Boolean).join(" / ");
  // card finalizado: só a conta pode ser completada (o resto está no protocolo)
  const editar = onPreencher
    ? (campo) => (card.status === "FECHADO" && campo !== "conta" ? null : (v) => onPreencher({ [campo]: v }))
    : () => null;
  const temQra = card.qra !== null && card.qra !== undefined;
  if (interno(card))
    return (
      <>
        <Linha rotulo="Demanda">
          <CampoEditavel texto={card.titulo} valorInicial={card.titulo} onSalvar={editar("titulo")} />
        </Linha>
        <Linha rotulo="Solicitante">
          <CampoEditavel texto={card.solicitante} valorInicial={card.solicitante} onSalvar={editar("solicitante")} />
        </Linha>
        <Linha rotulo="Conta">
          <CampoEditavel
            texto={card.conta && `${card.conta} — ${card.filial || "não localizada"}${card.grupo ? ` (${card.grupo})` : ""}`}
            valorInicial={card.conta} placeholder="opcional" onSalvar={editar("conta")}
          />
          <CadastrarNaBase card={card} onPreencher={onPreencher} />
        </Linha>
        <Linha rotulo="Endereço">{end}</Linha>
        <Linha rotulo="Solicitação">{card.observacao_inicial}</Linha>
        <Linha rotulo="Aberto">{`${card.analista || "-"} em ${dataBR(card.inicio)}`}</Linha>
        {card.fim && <Linha rotulo="Concluído">{`${card.analista_fim || "-"} em ${dataBR(card.fim)}`}</Linha>}
        <Linha rotulo="Conclusão">{card.observacao}</Linha>
      </>
    );
  return (
    <>
      <Linha rotulo="O.S">
        <CampoEditavel texto={card.os} valorInicial={card.os} placeholder="059127/26" onSalvar={editar("os")} />
      </Linha>
      <Linha rotulo="Conta">
        <CampoEditavel
          texto={card.conta && `${card.conta} — ${card.filial || "não localizada"}${card.grupo ? ` (${card.grupo})` : ""}`}
          valorInicial={card.conta} placeholder="B555" onSalvar={editar("conta")}
        />
        <CadastrarNaBase card={card} onPreencher={onPreencher} />
      </Linha>
      <Linha rotulo="Tipo">
        <CampoEditavel texto={card.tipo} valorInicial={card.tipo} opcoes={listas.tipo} onSalvar={editar("tipo")} />
      </Linha>
      <Linha rotulo="Sistema">
        <CampoEditavel texto={card.sistema} valorInicial={card.sistema} opcoes={listas.sistema} onSalvar={editar("sistema")} />
      </Linha>
      <Linha rotulo="Endereço">{end}</Linha>
      <Linha rotulo="Equipamento">{equip}</Linha>
      <Linha rotulo="Técnico">
        <CampoEditavel
          texto={temQra && `QRA ${card.qra} — ${card.tecnico || "NÃO LOCALIZADO"}${card.status_tecnico ? ` (${card.status_tecnico})` : ""}`}
          valorInicial={temQra ? card.qra : ""} tipo="number" placeholder="QRA"
          onSalvar={editar("qra") && ((v) => editar("qra")(Number(v)))}
        />
      </Linha>
      <Linha rotulo="Contato">{card.contato_tecnico}</Linha>
      <Linha rotulo="Aberto">{`${card.analista || "-"} em ${dataBR(card.inicio)}`}</Linha>
      <Linha rotulo="Obs. inicial">{card.observacao_inicial}</Linha>
      <Linha rotulo="Status O.S">{card.status_os}</Linha>
      {(card.causa || card.status === "FECHADO") && (
        <Linha rotulo="Causa / solução">
          <CampoEditavel texto={card.causa} valorInicial={card.causa} opcoes={listas.causa} placeholder="informar"
            onSalvar={onPreencher && (card.status !== "FECHADO" || gestao) ? (v) => onPreencher({ causa: v }) : null} />
        </Linha>
      )}
      {card.fim && <Linha rotulo="Finalizado">{`${card.analista_fim || "-"} em ${dataBR(card.fim)}`}</Linha>}
      <Linha rotulo="O.S filha">{card.os_filha}</Linha>
      <Linha rotulo="Protocolo">{card.protocolo || card.protocolo_legado}</Linha>
      <Linha rotulo="Observação">{card.observacao}</Linha>
      {card.origem === "planilha" && <Linha rotulo="Origem">{`planilha (linha ${card.linha_planilha})`}</Linha>}
    </>
  );
};

export const Andamentos = ({ card, onAtualizado }) => {
  const c = useStyles();
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const registrar = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/andamentos`, { texto });
      setTexto("");
      onAtualizado && onAtualizado(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setEnviando(false);
  };
  return (
    <>
      {(card.andamentos || []).length === 0 && (
        <Typography variant="body2" color="textSecondary">Sem andamentos.</Typography>
      )}
      {(card.andamentos || []).map((x) => (
        <div key={x.id} className={`${c.andamento} ${x.origem === "whatsapp" ? c.andamentoWa : ""}`}>
          <div className={c.meta}>{`${dataBR(x.criado_em)} · ${x.analista}${x.origem === "whatsapp" ? " · WhatsApp" : ""}`}</div>
          {x.midia ? <MidiaAndamento midia={x.midia} /> : <div style={{ whiteSpace: "pre-wrap" }}>{x.texto}</div>}
        </div>
      ))}
      {(
        <form onSubmit={registrar}>
          <TextField
            fullWidth size="small" variant="outlined"
            placeholder={card.status === "FECHADO" ? "Adicionar informação (card finalizado)…" : "Registrar andamento…"}
            value={texto} onChange={(e) => setTexto(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" type="submit" disabled={enviando}><Send fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
          />
        </form>
      )}
    </>
  );
};

// ------------------------------------------------------------------ corrigir dados
export const DialogCorrigir = ({ card, open, onClose, onSalvo }) => {
  const listas = useListas();
  const [f, setF] = useState({});
  const [clientes, setClientes] = useState([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({
      qra: card.qra ?? "", conta: card.conta || "", cliente_id: card.cliente_id || "",
      os: card.os || "", inicio: card.inicio ? String(card.inicio).slice(0, 16) : agoraLocal(),
      tipo: card.tipo || "", sistema: card.sistema || "", observacao: card.observacao_inicial || "",
    });
    setClientes([]);
  }, [open, card]);

  const buscarConta = async () => {
    if (!f.conta) return setClientes([]);
    try {
      const { data } = await suporteApi.get(`/api/clientes/conta/${encodeURIComponent(f.conta)}`);
      setClientes(data);
      if (!data.some((x) => x.id === f.cliente_id)) setF((s) => ({ ...s, cliente_id: data[0]?.id || "" }));
    } catch {
      setClientes([]);
      toast.warn("Conta não cadastrada na Base clientes");
    }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const { data } = await suporteApi.put(`/api/atendimentos/${card.id}`, {
        ...f, qra: Number(f.qra), cliente_id: f.cliente_id || null,
      });
      onSalvo(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };

  const campo = (nome) => ({
    value: f[nome] ?? "", onChange: (e) => setF({ ...f, [nome]: e.target.value }),
    fullWidth: true, size: "small", variant: "outlined", margin: "dense",
  });
  const opcoes = (itens, atual) => (atual && !itens.includes(atual) ? [atual, ...itens] : itens);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Corrigir dados do atendimento</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={4}><TextField label="QRA" type="number" {...campo("qra")} /></Grid>
          <Grid item xs={8}><TextField label="Conta" {...campo("conta")} onBlur={buscarConta} /></Grid>
          {clientes.length > 1 && (
            <Grid item xs={12}>
              <TextField select label="Cadastro da conta" {...campo("cliente_id")}>
                {clientes.map((c) => <MenuItem key={c.id} value={c.id}>{`${c.nome} (${c.status})`}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6}><TextField label="O.S (texto livre)" {...campo("os")} /></Grid>
          <Grid item xs={6}>
            <TextField label="Início" type="datetime-local" InputLabelProps={{ shrink: true }} {...campo("inicio")} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Tipo de serviço" {...campo("tipo")}>
              {opcoes(listas.tipo, f.tipo).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Sistema" {...campo("sistema")}>
              {opcoes(listas.sistema, f.sistema).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField label="Observação inicial" multiline rows={2} {...campo("observacao")} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={salvar} disabled={salvando}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------ finalizar
export const DialogFinalizar = ({ card, open, onClose, onFinalizado }) => {
  const listas = useListas();
  const [f, setF] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) setF({ fim: agoraLocal(), status_os: "FIM DE TESTE", os_filha: "", observacao: "", causa: "",
      enviar_whatsapp: true, resolver_whatsapp: true });
  }, [open]);

  const finalizar = async () => {
    setSalvando(true);
    try {
      const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/finalizar`, {
        ...f, enviar_whatsapp: !!(card.whatsapp_destino && f.enviar_whatsapp),
        resolver_whatsapp: !!(card.whatsapp_ticket_id && f.resolver_whatsapp),
      });
      toast.success(interno(card) ? "Chamado concluído" : `Chamado concluído · protocolo ${data.protocolo}`);
      onFinalizado(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };
  const campo = (nome) => ({
    value: f[nome] ?? "", onChange: (e) => setF({ ...f, [nome]: e.target.value }),
    fullWidth: true, size: "small", variant: "outlined", margin: "dense",
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{interno(card) ? "Concluir chamado interno" : "Finalizar e gerar protocolo"}</DialogTitle>
      <DialogContent>
        {incompleto(card) && (
          <Typography color="error" variant="body2">
            {`Complete antes de ${interno(card) ? "concluir" : "finalizar"}: ${faltandoNoCard(card).join(", ")}.`}
          </Typography>
        )}
        {card.protocolo && (
          <Typography variant="body2" style={{ background: "rgba(63,81,181,0.08)", padding: "6px 8px", borderRadius: 6, marginBottom: 6 }}>
            {`Protocolo já gerado: ${card.protocolo}. Ele será mantido, com o término registrado na geração.`}
          </Typography>
        )}
        <Grid container spacing={1}>
          {!card.protocolo && <Grid item xs={6}>
            <TextField label="Término" type="datetime-local" InputLabelProps={{ shrink: true }} {...campo("fim")} />
          </Grid>}
          {!interno(card) && <Grid item xs={6}>
            <TextField select label="Status da O.S" {...campo("status_os")}>
              {(listas.status.length ? listas.status : ["FIM DE TESTE"]).map((x) => (
                <MenuItem key={x} value={x}>{x}</MenuItem>
              ))}
            </TextField>
          </Grid>}
          {!interno(card) && (listas.causa || []).length > 0 && <Grid item xs={12}>
            <TextField select required label="Causa / solução" {...campo("causa")}
              helperText="Alimenta o relatório de causas mais comuns">
              {listas.causa.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>}
          {card.tipo === "CONDENAÇÃO" && (
            <Grid item xs={12}><TextField label="O.S filha (se já tiver)" {...campo("os_filha")} /></Grid>
          )}
          <Grid item xs={12}>
            <TextField label={interno(card) ? "O que foi feito" : "Observação final"} multiline rows={4} required {...campo("observacao")} />
          </Grid>
        </Grid>
        {card.whatsapp_destino && !interno(card) && (
          <FormControlLabel
            control={<Checkbox checked={!!f.enviar_whatsapp} color="primary"
              onChange={(e) => setF({ ...f, enviar_whatsapp: e.target.checked })} />}
            label={`Enviar ao técnico o protocolo com o aviso de chamado concluído (${card.whatsapp_destino})`}
          />
        )}
        {card.whatsapp_ticket_id && (
          <FormControlLabel
            control={<Checkbox checked={!!f.resolver_whatsapp} color="primary"
              onChange={(e) => setF({ ...f, resolver_whatsapp: e.target.checked })} />}
            label={`Resolver a conversa #${card.whatsapp_ticket_id} no WhatsApp (depois de enviar o protocolo)`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={finalizar}
          disabled={salvando || !f.observacao?.trim() || incompleto(card) || (!interno(card) && (listas.causa || []).length > 0 && !f.causa)}>
          {interno(card) ? "Concluir" : "Finalizar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------ campos adicionais do card do suporte
let extrasSuporte = null;
export const carregarExtrasSuporte = (forcar = false) => {
  if (!extrasSuporte || forcar) {
    extrasSuporte = suporteApi.get("/api/modelos-card/suporte").then((r) => r.data).catch(() => ({ campos: [], opcoes: {} }));
  }
  return extrasSuporte;
};

const CamposAdicionais = ({ card, onAtualizado }) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    carregarExtrasSuporte().then(setM);
  }, []);
  if (!m || !(m.campos || []).length) return null;
  const salvar = async (id, v) => {
    try {
      const { data } = await suporteApi.patch(`/api/atendimentos/${card.id}/campos`, { campos: { [id]: v } });
      onAtualizado(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  return (
    <div style={{ marginTop: 6 }}>
      {m.campos.map((cp) => (
        <CampoCard key={cp.id} campo={cp} valor={(card.campos || {})[cp.id]} opcoes={m.opcoes?.[cp.lista]}
          bloqueado={card.status === "FECHADO" && !!card.fila_id} onSalvar={salvar} />
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ protocolo sem finalizar
export const DialogProtocolo = ({ card, open, onClose, onAtualizado }) => {
  const [r, setR] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    if (!open) return;
    setR(null);
    suporteApi.post(`/api/atendimentos/${card.id}/protocolo`, { enviar_whatsapp: false })
      .then(({ data }) => { setR(data); onAtualizado && onAtualizado(data.card); })
      .catch((err) => { toast.error(mensagemErro(err)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card.id]);
  const enviar = async () => {
    setEnviando(true);
    try {
      await suporteApi.post(`/api/atendimentos/${card.id}/protocolo`, { enviar_whatsapp: true });
      toast.success("Enviando ao técnico pelo WhatsApp…");
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setEnviando(false);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{r && !r.previo ? "Protocolo do chamado concluído" : "Protocolo (chamado continua aberto)"}</DialogTitle>
      <DialogContent dividers>
        {!r ? <CircularProgress size={24} /> : (
          <>
            <Typography variant="caption" color="textSecondary">Resposta pronta</Typography>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, margin: "6px 0 0", padding: 12,
              borderRadius: 8, background: "rgba(0,0,0,0.04)" }}>{r.texto}</pre>
            {r.previo && (
              <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
                Ao finalizar o card, este mesmo protocolo é mantido e o técnico recebe o aviso de chamado concluído.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button disabled={!r} startIcon={<FileCopyOutlined />} onClick={() => copiar(r.texto)}>Copiar texto</Button>
        {card.whatsapp_destino && (
          <Button color="primary" variant="contained" startIcon={<Send />} disabled={!r || enviando} onClick={enviar}>
            Enviar ao técnico
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------ continuação (mesma loja / O.S)
// A mesma O.S não abre outro card; admin/supervisor marca como pendente e libera UM card de continuação.
const DialogLiberar = ({ card, open, onClose, onLiberado }) => {
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (open) setMotivo(""); }, [open]);
  const salvar = async () => {
    setSalvando(true);
    try {
      const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/liberar-continuacao`, { motivo });
      toast.success("Card marcado como pendente: já pode abrir o card de continuação");
      onLiberado(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Marcar como pendente</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {`O card #${card.id} (O.S ${card.os || "-"}) passa a PENDENTE e fica liberado UM card de continuação para a mesma loja / O.S. `
            + "O novo card já vem com O.S, conta, tipo e sistema e fica ligado a este."}
        </Typography>
        <TextField id="lib-motivo" label="Motivo" required fullWidth multiline rows={2} variant="outlined" margin="dense"
          placeholder="Ex.: técnico volta à loja no período noturno" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={salvando || motivo.trim().length < 3} onClick={salvar}>
          Marcar como pendente
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Continuacao = ({ card, onAbrir, onLiberar }) => {
  const gestao = usePermissao("cards.liberar_continuacao");
  if (interno(card) || card.fila_id) return null;
  const pode = gestao && card.status === "FECHADO" && !card.continuado_em && !card.continuacao_liberada;
  if (!card.continuacao_de && !card.continuado_em && !card.continuacao_liberada && !pode) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", margin: "6px 0" }}>
      {card.continuacao_de && (
        <Chip size="small" color="primary" icon={<CallSplit />} clickable onClick={() => onAbrir(card.continuacao_de)}
          label={`Continuação do card #${card.continuacao_de}`} />
      )}
      {card.continuado_em && (
        <Chip size="small" color="primary" variant="outlined" icon={<CallSplit />} clickable onClick={() => onAbrir(card.continuado_em)}
          label={`Continuado no card #${card.continuado_em}`} />
      )}
      {card.continuacao_liberada && (
        <Chip size="small" style={{ background: "#fff3e0", color: "#e65100" }}
          label={card.liberado_continuacao_por
            ? `Pendente: liberado para continuação por ${card.liberado_continuacao_por}${card.liberado_continuacao_motivo ? ` (${card.liberado_continuacao_motivo})` : ""}`
            : "Pendente: liberado para continuação"} />
      )}
      {pode && (
        <Button size="small" variant="outlined" startIcon={<CallSplit />} onClick={onLiberar}>
          Marcar como pendente (continuar em novo card)
        </Button>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ painel completo do card
export const PainelCard = ({ card, onAtualizado }) => {
  const c = useStyles();
  const [liberar, setLiberar] = useState(false);
  const [ligado, setLigado] = useState(null); // card da continuação aberto por cima
  const [corrigir, setCorrigir] = useState(false);
  const [finalizar, setFinalizar] = useState(false);
  const [protocolo, setProtocolo] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const permissoes = usePermissoes();
  const podeEditar = tem(permissoes, "cards.editar");
  const podeFinalizar = tem(permissoes, "cards.finalizar");

  // edição direto na gaveta: salva só o campo clicado
  const preencher = async (dados) => {
    try {
      const { data } = await suporteApi.patch(`/api/atendimentos/${card.id}`, dados);
      onAtualizado(data);
      if (dados.conta && !data.filial) toast.warn(`Conta ${data.conta} não está na Base clientes`);
      return true;
    } catch (err) {
      toast.error(mensagemErro(err));
      return false;
    }
  };

  const acao = async (fn) => {
    setOcupado(true);
    try {
      await fn();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setOcupado(false);
  };
  const recarregar = () => acao(async () => {
    const { data } = await suporteApi.get(`/api/atendimentos/${card.id}`);
    onAtualizado(data);
  });

  return (
    <Box>
      <Etiquetas card={card} />
      <Continuacao card={card} onAbrir={setLigado} onLiberar={() => setLiberar(true)} />
      {card.protocolo && (
        <div className={c.protocolo}>
          <div>
            <Typography variant="caption" color="textSecondary">
              {card.status === "FECHADO" ? "Protocolo" : "Protocolo gerado · chamado ainda aberto"}
            </Typography>
            <div className={c.codigo}>{card.protocolo}</div>
            <div className={c.acoes}>
              <Button size="small" variant="outlined" startIcon={<FileCopyOutlined />}
                onClick={() => copiar(card.protocolo)}>Copiar</Button>
              <Button size="small" variant="outlined" onClick={() => setProtocolo(true)}>Resposta pronta</Button>
              {card.whatsapp_destino && podeFinalizar && (
                <Button size="small" variant="outlined" startIcon={<Send />} disabled={ocupado}
                  onClick={() => acao(async () => {
                    await suporteApi.post(`/api/atendimentos/${card.id}/enviar-whatsapp`);
                    toast.info("Enviando pelo WhatsApp…");
                    setTimeout(recarregar, 5000);
                  })}>
                  {card.status === "FECHADO" ? "Reenviar (chamado concluído)" : "Enviar no WhatsApp"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <DadosCard card={card} onPreencher={podeEditar ? preencher : null} />
      <CamposAdicionais card={card} onAtualizado={onAtualizado} />
      {card.status !== "FECHADO" && (
        <div className={c.acoes}>
          {!interno(card) && podeEditar && (
            <Button size="small" variant="outlined" startIcon={<EditOutlined />} onClick={() => setCorrigir(true)}>
              Corrigir dados
            </Button>
          )}
          {card.status === "PENDENTE" && podeEditar && (
            <Button size="small" variant="outlined" startIcon={<Replay />} disabled={ocupado}
              onClick={() => acao(async () => {
                const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/reabrir`);
                onAtualizado(data);
              })}>
              Trazer para o quadro
            </Button>
          )}
          {!interno(card) && !card.protocolo && podeFinalizar && (
            <Button size="small" variant="outlined" disabled={incompleto(card)} onClick={() => setProtocolo(true)}
              title={incompleto(card) ? "Complete QRA, O.S, tipo, sistema, a conta da Base clientes e o Cliente" : "Gera o protocolo sem finalizar o chamado"}>
              Gerar protocolo
            </Button>
          )}
          {podeFinalizar && (
            <Button size="small" variant="contained" color="primary" startIcon={<CheckCircleOutline />}
              onClick={() => setFinalizar(true)}>
              {interno(card) ? "Concluir" : "Finalizar"}
            </Button>
          )}
          {ocupado && <CircularProgress size={20} />}
        </div>
      )}
      <FormulariosDoCard card={card} onCardMudou={recarregar} />
      <Divider style={{ margin: "10px 0" }} />
      <Typography variant="subtitle2" color="textSecondary">ANDAMENTO</Typography>
      <Andamentos card={card} onAtualizado={onAtualizado} />
      <DialogCorrigir card={card} open={corrigir} onClose={() => setCorrigir(false)} onSalvo={onAtualizado} />
      <DialogFinalizar card={card} open={finalizar} onClose={() => setFinalizar(false)} onFinalizado={onAtualizado} />
      <DialogProtocolo card={card} open={protocolo} onClose={() => setProtocolo(false)} onAtualizado={onAtualizado} />
      <DialogLiberar card={card} open={liberar} onClose={() => setLiberar(false)} onLiberado={onAtualizado} />
      {ligado && <DialogCard id={ligado} open onClose={() => setLigado(null)} />}
    </Box>
  );
};

// Janela com o painel do card (usada no Quadro e no Histórico)
export const DialogCard = ({ id, open, onClose, onMudou }) => {
  const [card, setCard] = useState(null);
  useEffect(() => {
    if (!open || !id) return;
    setCard(null);
    suporteApi.get(`/api/atendimentos/${id}`).then((r) => setCard(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, [open, id]);
  const atualizado = (c) => {
    setCard(c);
    onMudou && onMudou(c);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {!card ? "Carregando…" : interno(card) ? `Interno · ${card.titulo}` : `O.S ${card.os || "a informar"}`}
      </DialogTitle>
      <DialogContent dividers>
        {card ? <PainelCard card={card} onAtualizado={atualizado} /> : <CircularProgress />}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};
